import React, { useCallback, useEffect, useState } from 'react';
import { adminSessionFetch } from '../utils/functionAuth';
import { showToast } from './Toast';
import {
  KNOWN_CODE,
  PLAN_OPTIONS,
  generateDiscountCode,
  planLabel,
  validateDiscountDraft,
} from '../shared/discountCodes';

const EMPTY = {
  code: '',
  type: 'percent',
  value: '',
  duration: 'once',
  months: '3',
  plans: [],
  expiresOn: '',
  maxRedemptions: '',
  firstTime: false,
};

function fieldStyle(colors) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.7rem 0.8rem',
    borderRadius: '0.7rem',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    font: 'inherit',
    fontSize: '1rem',
  };
}

function formatDay(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function draftTerms(value) {
  const discount = value.type === 'percent'
    ? `${value.percent}% off`
    : `$${(value.cents / 100).toFixed(2)} off`;
  if (value.duration === 'once') return `${discount} once`;
  if (value.duration === 'forever') return `${discount} forever`;
  if (value.duration === 'repeating' && value.months) {
    return `${discount} for ${value.months} month${value.months === 1 ? '' : 's'}`;
  }
  return discount;
}

function productLine(code) {
  const products = Array.isArray(code.products) ? code.products : [];
  const names = products.map((product) => {
    if (typeof product === 'string') return product;
    return (product && (product.name || product.id)) || '';
  }).filter(Boolean);
  if (names.length) return names.join(', ');
  const plans = (code.plans || []).filter(Boolean);
  if (plans.length) return plans.join(', ');
  return 'All products';
}

function redeemedCount(code) {
  if (code.timesRedeemed != null && code.timesRedeemed !== '') return Number(code.timesRedeemed) || 0;
  return Number(code.timesUsed) || 0;
}

function initialHomeowner(preview, homeownerOpen, initialMode) {
  if (homeownerOpen === false) return false;
  if (homeownerOpen === true) return true;
  if (initialMode === 'live') return false;
  if (initialMode === 'test' || preview) return true;
  return false;
}

function DiscountCodesPanel({ colors, narrow, preview, initialCodes, initialMode, homeownerOpen }) {
  const ink = colors || {};
  const [codes, setCodes] = useState(initialCodes || []);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [knownCode, setKnownCode] = useState(KNOWN_CODE);
  const [mode, setMode] = useState(initialMode === 'live' || initialMode === 'test' ? initialMode : (preview ? 'test' : ''));
  const [showHomeowner, setShowHomeowner] = useState(initialHomeowner(preview, homeownerOpen, initialMode));
  const [pendingOff, setPendingOff] = useState('');

  const applyResult = useCallback((result) => {
    if (!result) return;
    setCodes(result.codes || []);
    if (result.knownCode) setKnownCode(result.knownCode);
    if (result.mode === 'live' || result.mode === 'test') setMode(result.mode);
    if (typeof result.homeowner === 'boolean') {
      setShowHomeowner(result.homeowner);
      if (!result.homeowner) {
        setForm((current) => ({
          ...current,
          plans: current.plans.filter((id) => id !== 'homeowner'),
        }));
      }
    }
  }, []);

  useEffect(() => {
    if (preview) return undefined;
    let live = true;
    (async () => {
      try {
        const response = await adminSessionFetch('/.netlify/functions/admin-discounts', { action: 'list' });
        const result = await response.json().catch(() => ({}));
        if (!live) return;
        if (!response.ok) throw new Error(result.error || 'Could not load discount codes');
        applyResult(result);
        setError('');
      } catch (loadError) {
        if (live) setError(loadError.message || 'Could not load discount codes');
      }
    })();
    return () => {
      live = false;
    };
  }, [preview, applyResult]);

  const post = async (body) => {
    if (preview) return null;
    const response = await adminSessionFetch('/.netlify/functions/admin-discounts', body);
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Could not update discount codes');
    applyResult(result);
    return result;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const draft = validateDiscountDraft({
      ...form,
      value: form.type === 'percent' ? Number(form.value) : form.value,
      months: Number(form.months),
      maxRedemptions: form.maxRedemptions,
      firstTime: form.firstTime,
    });
    if (!draft.ok) {
      setError(draft.error);
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (preview) {
        setCodes((current) => [{
          id: `local-${draft.value.code}`,
          code: draft.value.code,
          active: true,
          status: 'Active',
          timesUsed: 0,
          timesRedeemed: 0,
          maxRedemptions: draft.value.maxRedemptions,
          terms: draftTerms(draft.value),
          discount: draft.value.type === 'percent' ? `${draft.value.percent}%` : `$${(draft.value.cents / 100).toFixed(2)}`,
          duration: draft.value.duration === 'once' ? 'Once' : draft.value.duration === 'forever' ? 'Forever' : `${draft.value.months} months`,
          plans: draft.value.plans.map(planLabel),
          products: draft.value.plans.map((id) => ({ id, name: planLabel(id) })),
          expiresAt: draft.value.expiresAt ? new Date(draft.value.expiresAt * 1000).toISOString() : '',
          createdAt: new Date().toISOString(),
          known: draft.value.code === knownCode,
        }, ...current]);
        setForm(EMPTY);
        return;
      }
      await post({ action: 'create', ...form, value: form.value, months: form.months });
      setForm(EMPTY);
      showToast('Discount code issued.', 'success');
    } catch (submitError) {
      setError(submitError.message || 'Could not issue that code');
    } finally {
      setBusy(false);
    }
  };

  const commitToggle = async (code, active) => {
    setBusy(true);
    setError('');
    setPendingOff('');
    try {
      if (preview) {
        setCodes((current) => current.map((item) => (
          item.id === code.id
            ? { ...item, active, status: active ? 'Active' : 'Inactive' }
            : item
        )));
        return;
      }
      await post({ action: 'set_active', id: code.id, active });
    } catch (toggleError) {
      setError(toggleError.message || 'Could not update that code');
    } finally {
      setBusy(false);
    }
  };

  const label = { display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.35rem', color: ink.text };
  const input = fieldStyle(ink);
  const planOptions = showHomeowner ? PLAN_OPTIONS : PLAN_OPTIONS.filter((option) => option.id !== 'homeowner');
  const modeWord = mode === 'live' ? 'Live' : (mode === 'test' ? 'Test' : '');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.85rem' }}>
        <h2 style={{ color: ink.text, fontSize: '1.35rem', fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>Discount codes</h2>
        {modeWord ? (
          <span
            aria-label="Stripe mode"
            style={{
              flex: '0 0 auto',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: ink.textSecondary,
              border: `1px solid ${ink.border}`,
              borderRadius: '999px',
              padding: '0.2rem 0.55rem',
              lineHeight: 1.3,
            }}
          >
            {modeWord}
          </span>
        ) : null}
      </div>
      {error ? <p style={{ color: '#b45309' }}>{error}</p> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1.35rem' }}>
        {codes.map((code) => {
          const used = redeemedCount(code);
          const max = code.maxRedemptions == null || code.maxRedemptions === '' ? null : code.maxRedemptions;
          const confirming = pendingOff === code.id;
          return (
            <article
              key={code.id}
              style={{
                background: ink.surface,
                border: `1px solid ${ink.border}`,
                borderRadius: '1rem',
                padding: '0.95rem 1rem',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '0.85rem',
                flexWrap: 'wrap',
                opacity: code.active ? 1 : 0.72,
              }}
            >
              <div style={{ minWidth: 0, flex: '1 1 16rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.55rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 650, letterSpacing: '-0.02em', color: ink.text, fontSize: '1.05rem' }}>{code.code}</span>
                  <span style={{ color: ink.textMuted, fontSize: '0.85rem' }}>{code.status}</span>
                </div>
                <div style={{ color: ink.text, marginTop: '0.28rem' }}>{code.terms || code.discount}</div>
                <div style={{ color: ink.textSecondary, marginTop: '0.15rem' }}>{productLine(code)}</div>
                <div style={{ color: ink.textMuted, marginTop: '0.4rem', fontSize: '0.85rem', lineHeight: 1.45 }}>
                  <div>Redeemed {used}</div>
                  <div>{max == null ? 'No max' : `Max ${max}`}</div>
                  <div>{code.expiresAt ? `Expires ${formatDay(code.expiresAt)}` : 'No expiry'}</div>
                  {code.createdAt ? <div>Created {formatDay(code.createdAt)}</div> : null}
                </div>
              </div>
              {confirming ? (
                <div style={{ alignSelf: 'center', display: 'flex', flexDirection: 'column', alignItems: narrow ? 'stretch' : 'flex-end', gap: '0.45rem' }}>
                  <span style={{ color: ink.textSecondary, fontSize: '0.9rem' }}>Turn off {code.code}?</span>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => commitToggle(code, false)}
                      style={{
                        background: ink.text || '#1a1a1a',
                        color: ink.background || '#fff',
                        border: 'none',
                        borderRadius: '999px',
                        padding: '0.45rem 0.85rem',
                        font: 'inherit',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Turn off
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setPendingOff('')}
                      style={{
                        background: 'transparent',
                        color: ink.text,
                        border: `1px solid ${ink.border}`,
                        borderRadius: '999px',
                        padding: '0.45rem 0.85rem',
                        font: 'inherit',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => (code.active ? setPendingOff(code.id) : commitToggle(code, true))}
                  style={{
                    alignSelf: 'center',
                    background: 'transparent',
                    color: ink.text,
                    border: `1px solid ${ink.border}`,
                    borderRadius: '999px',
                    padding: '0.45rem 0.85rem',
                    font: 'inherit',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {code.active ? 'Turn off' : 'Turn on'}
                </button>
              )}
            </article>
          );
        })}
        {codes.length === 0 ? <p style={{ color: ink.textSecondary, marginTop: 0 }}>No discount codes yet.</p> : null}
      </div>

      <form
        onSubmit={onSubmit}
        style={{
          background: ink.surface,
          border: `1px solid ${ink.border}`,
          borderRadius: '1rem',
          padding: '1rem',
          display: 'grid',
          gap: '0.85rem',
        }}
      >
        <div style={{ color: ink.text, fontWeight: 600 }}>New code</div>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr auto', gap: '0.6rem', alignItems: 'end' }}>
          <label>
            <span style={label}>Code</span>
            <input
              aria-label="Code"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
              placeholder="Leave blank to generate"
              style={input}
            />
          </label>
          <button
            type="button"
            onClick={() => setForm({ ...form, code: generateDiscountCode() })}
            style={{ ...input, width: 'auto', background: 'transparent', color: ink.primary || '#59c4c4', fontWeight: 600, cursor: 'pointer' }}
          >
            Generate
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr 1fr', gap: '0.6rem' }}>
          <label>
            <span style={label}>Discount</span>
            <select aria-label="Discount type" value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })} style={input}>
              <option value="percent">Percent</option>
              <option value="amount">Fixed amount</option>
            </select>
          </label>
          <label>
            <span style={label}>{form.type === 'percent' ? 'Percent' : 'Amount in dollars'}</span>
            <input
              aria-label="Discount value"
              inputMode="decimal"
              value={form.value}
              onChange={(event) => setForm({ ...form, value: event.target.value })}
              style={input}
            />
          </label>
          <label>
            <span style={label}>Duration</span>
            <select aria-label="Duration" value={form.duration} onChange={(event) => setForm({ ...form, duration: event.target.value })} style={input}>
              <option value="once">Once</option>
              <option value="repeating">Repeating</option>
              <option value="forever">Forever</option>
            </select>
          </label>
        </div>
        {form.duration === 'repeating' ? (
          <label>
            <span style={label}>Months</span>
            <input
              aria-label="Months"
              inputMode="numeric"
              value={form.months}
              onChange={(event) => setForm({ ...form, months: event.target.value })}
              style={{ ...input, maxWidth: '8rem' }}
            />
          </label>
        ) : null}
        <fieldset style={{ border: `1px solid ${ink.border}`, borderRadius: '0.8rem', margin: 0, padding: '0.75rem' }}>
          <legend style={{ ...label, padding: '0 0.25rem' }}>Applies to</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1rem' }}>
            {planOptions.map((option) => (
              <label key={option.id} style={{ color: ink.text, display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.plans.indexOf(option.id) !== -1}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? form.plans.concat(option.id)
                      : form.plans.filter((id) => id !== option.id);
                    setForm({ ...form, plans: next });
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
          <label>
            <span style={label}>Expiration, optional</span>
            <input
              aria-label="Expiration date"
              type="date"
              value={form.expiresOn}
              onChange={(event) => setForm({ ...form, expiresOn: event.target.value })}
              style={input}
            />
          </label>
          <label>
            <span style={label}>Max redemptions, optional</span>
            <input
              aria-label="Max redemptions"
              inputMode="numeric"
              value={form.maxRedemptions}
              onChange={(event) => setForm({ ...form, maxRedemptions: event.target.value })}
              style={input}
            />
          </label>
        </div>
        <label style={{ color: ink.text, display: 'flex', gap: '0.45rem', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={form.firstTime}
            onChange={(event) => setForm({ ...form, firstTime: event.target.checked })}
          />
          First-time customers only
        </label>
        <button
          type="submit"
          disabled={busy}
          style={{
            justifySelf: 'start',
            background: ink.primary || '#59c4c4',
            color: '#083344',
            border: 'none',
            borderRadius: '999px',
            padding: '0.7rem 1.15rem',
            font: 'inherit',
            fontWeight: 650,
            cursor: 'pointer',
          }}
        >
          {busy ? 'Saving' : 'Issue code'}
        </button>
      </form>
    </div>
  );
}

export default DiscountCodesPanel;
